import realmOfShadows from "@/maple-market-hub/src/assets/apps/realm-of-shadows.jpg";
import docuflow from "@/maple-market-hub/src/assets/apps/docuflow.jpg";
import novaCommand from "@/maple-market-hub/src/assets/apps/nova-command.jpg";
import pixelforge from "@/maple-market-hub/src/assets/apps/pixelforge.jpg";
import soundlab from "@/maple-market-hub/src/assets/apps/soundlab.jpg";
import velocityRush from "@/maple-market-hub/src/assets/apps/velocity-rush.jpg";
import codevault from "@/maple-market-hub/src/assets/apps/codevault.jpg";
import harvestValley from "@/maple-market-hub/src/assets/apps/harvest-valley.jpg";
import formcraft from "@/maple-market-hub/src/assets/apps/formcraft.jpg";
import neonDistrict from "@/maple-market-hub/src/assets/apps/neon-district.jpg";
import datagrid from "@/maple-market-hub/src/assets/apps/datagrid.jpg";
import echoesOfLight from "@/maple-market-hub/src/assets/apps/echoes-of-light.jpg";
import heroBanner from "@/maple-market-hub/src/assets/hero-banner.jpg";

export type MapleMarketCategory = "Games" | "Office" | "Creative" | "Developer" | "Music" | "Utilities";
export type MapleMarketPackageType = 'bundle' | 'package' | 'none';
export type MapleOSEditionSku = 'mapleos-26' | 'mapleos-pro-26' | 'mapleos-enterprise-26';
export type MarketplaceVisibilityStatus = 'hidden' | 'unpublished' | 'published';
export type MarketplacePublishRequestStatus = 'none' | 'pending' | 'approved' | 'rejected';

export const MAPLE_OS_EDITION_OPTIONS: { value: MapleOSEditionSku; label: string }[] = [
  { value: 'mapleos-26', label: 'MapleOS 26' },
  { value: 'mapleos-pro-26', label: 'MapleOS Pro 26' },
  { value: 'mapleos-enterprise-26', label: 'MapleOS Enterprise 26' },
];

export interface MapleMarketApp {
  id: string;
  sku: string;
  name: string;
  developer: string;
  description: string;
  longDescription: string;
  price: number | 'Free';
  priceCents: number;
  isFree: boolean;
  image: string;
  heroImageUrl: string | null;
  thumbnailUrl: string | null;
  category: MapleMarketCategory;
  rating: number;
  reviews: number;
  tags: string[];
  size: string;
  releaseDate: string;
  featured?: boolean;
  version: string;
  packageType: MapleMarketPackageType;
  packagePath: string | null;
  includesWebInstaller: boolean;
  includesDesktopInstaller: boolean;
  includesBareMetalInstaller: boolean;
  supportedMapleOSEditions: MapleOSEditionSku[];
  visibilityStatus: MarketplaceVisibilityStatus;
  publishRequestStatus: MarketplacePublishRequestStatus;
  publishRequestedAt: string | null;
  publishRequestedBy: string | null;
  publishApprovedAt: string | null;
  publishApprovedBy: string | null;
  publishRejectedAt: string | null;
  publishRejectedBy: string | null;
  publishRejectionReason: string | null;
  firstPublishedAt: string | null;
  hasBeenPublished: boolean;
}

type MapleMarketFallbackEntry = Omit<
  MapleMarketApp,
  | 'heroImageUrl'
  | 'thumbnailUrl'
  | 'supportedMapleOSEditions'
  | 'visibilityStatus'
  | 'publishRequestStatus'
  | 'publishRequestedAt'
  | 'publishRequestedBy'
  | 'publishApprovedAt'
  | 'publishApprovedBy'
  | 'publishRejectedAt'
  | 'publishRejectedBy'
  | 'publishRejectionReason'
  | 'firstPublishedAt'
  | 'hasBeenPublished'
> & {
  supportedMapleOSEditions?: MapleOSEditionSku[];
};

type MarketplaceProductRecord = {
  id?: string;
  sku: string;
  name?: string | null;
  developer?: string | null;
  short_description?: string | null;
  long_description?: string | null;
  price_cents?: number | null;
  is_free?: boolean | null;
  category?: string | null;
  rating?: number | string | null;
  reviews?: number | null;
  tags?: string[] | null;
  size_text?: string | null;
  release_date?: string | null;
  featured?: boolean | null;
  version?: string | null;
  package_type?: MapleMarketPackageType | null;
  hero_image_url?: string | null;
  thumbnail_url?: string | null;
  package_path?: string | null;
  includes_web_installer?: boolean | null;
  includes_desktop_installer?: boolean | null;
  includes_bare_metal_installer?: boolean | null;
  supported_mapleos_editions?: MapleOSEditionSku[] | null;
  visibility_status?: MarketplaceVisibilityStatus | null;
  publish_request_status?: MarketplacePublishRequestStatus | null;
  publish_requested_at?: string | null;
  publish_requested_by?: string | null;
  publish_approved_at?: string | null;
  publish_approved_by?: string | null;
  publish_rejected_at?: string | null;
  publish_rejected_by?: string | null;
  publish_rejection_reason?: string | null;
  first_published_at?: string | null;
  has_been_published?: boolean | null;
};

export const mapleMarketHeroBanner = heroBanner;

export const mapleMarketCategoryMeta: { name: MapleMarketCategory; icon: string }[] = [
  { name: 'Games', icon: '🎮' },
  { name: 'Office', icon: '📄' },
  { name: 'Creative', icon: '🎨' },
  { name: 'Developer', icon: '💻' },
  { name: 'Music', icon: '🎵' },
  { name: 'Utilities', icon: '🔧' },
];

const fallbackCatalog: MapleMarketFallbackEntry[] = [
  {
    id: 'realm-of-shadows',
    sku: 'realm-of-shadows',
    name: 'Realm of Shadows',
    developer: 'Darkforge Studios',
    description: 'An epic open-world fantasy RPG where darkness consumes the realm and only you can restore the light.',
    longDescription: 'Embark on a sprawling adventure through a hand-crafted world teetering on the edge of destruction. Realm of Shadows combines deep character customization, tactical combat, and a branching narrative that reacts to your every choice.',
    price: 49.99,
    priceCents: 4999,
    isFree: false,
    image: realmOfShadows,
    category: 'Games',
    rating: 4.8,
    reviews: 12847,
    tags: ['RPG', 'Open World', 'Fantasy', 'Action'],
    size: '42.3 GB',
    releaseDate: '2025-11-15',
    featured: true,
    version: '1.0.0',
    packageType: 'none',
    packagePath: null,
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
    supportedMapleOSEditions: ['mapleos-26', 'mapleos-pro-26', 'mapleos-enterprise-26'],
  },
  {
    id: 'docuflow',
    sku: 'docuflow',
    name: 'DocuFlow Suite',
    developer: 'Maple Software Co.',
    description: 'The complete office suite for MapleOS. Documents, spreadsheets, presentations — everything you need.',
    longDescription: 'DocuFlow Suite is the definitive productivity toolkit built natively for MapleOS. Create professional documents, complex spreadsheets, and presentations in one seamless package.',
    price: 'Free',
    priceCents: 0,
    isFree: true,
    image: docuflow,
    category: 'Office',
    rating: 4.5,
    reviews: 34291,
    tags: ['Productivity', 'Documents', 'Office', 'Collaboration'],
    size: '890 MB',
    releaseDate: '2025-03-01',
    featured: true,
    version: '1.0.0',
    packageType: 'none',
    packagePath: null,
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
  },
  {
    id: 'nova-command',
    sku: 'nova-command',
    name: 'Nova Command',
    developer: 'Stellar Interactive',
    description: 'Command your fleet across the galaxy in this real-time space strategy epic.',
    longDescription: 'Take control of an interstellar empire in Nova Command. Build massive fleets, research devastating technologies, and outmaneuver rival factions in solar-system-scale conflicts.',
    price: 39.99,
    priceCents: 3999,
    isFree: false,
    image: novaCommand,
    category: 'Games',
    rating: 4.6,
    reviews: 8453,
    tags: ['Strategy', 'Sci-Fi', 'Space', 'RTS'],
    size: '28.7 GB',
    releaseDate: '2025-08-22',
    featured: false,
    version: '1.0.0',
    packageType: 'none',
    packagePath: null,
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
  },
  {
    id: 'pixelforge',
    sku: 'pixelforge',
    name: 'PixelForge',
    developer: 'Creative Labs Inc.',
    description: 'Professional photo and video editing with AI-powered tools. From quick fixes to cinema-grade production.',
    longDescription: 'PixelForge is the creative professional\'s tool of choice on MapleOS with GPU-accelerated rendering, AI-assisted editing, and non-destructive workflows.',
    price: 29.99,
    priceCents: 2999,
    isFree: false,
    image: pixelforge,
    category: 'Creative',
    rating: 4.7,
    reviews: 19823,
    tags: ['Photo Editing', 'Video', 'Creative', 'AI'],
    size: '2.1 GB',
    releaseDate: '2025-05-10',
    featured: true,
    version: '1.0.0',
    packageType: 'none',
    packagePath: null,
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
  },
  {
    id: 'soundlab-pro',
    sku: 'soundlab-pro',
    name: 'SoundLab Pro',
    developer: 'AudioWave Technologies',
    description: 'Professional digital audio workstation for music production, mixing, and mastering.',
    longDescription: 'SoundLab Pro is a complete music production environment with virtual instruments, effects, and mixing tools used by professionals.',
    price: 59.99,
    priceCents: 5999,
    isFree: false,
    image: soundlab,
    category: 'Music',
    rating: 4.9,
    reviews: 6721,
    tags: ['Music', 'DAW', 'Audio', 'Production'],
    size: '4.8 GB',
    releaseDate: '2025-01-20',
    featured: false,
    version: '1.0.0',
    packageType: 'none',
    packagePath: null,
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
  },
  {
    id: 'velocity-rush',
    sku: 'velocity-rush',
    name: 'Velocity Rush',
    developer: 'Nitro Games',
    description: 'Feel the speed in this hyper-realistic racing game. 200+ licensed cars, dynamic weather, and global multiplayer.',
    longDescription: 'Velocity Rush delivers a high-speed racing experience with detailed vehicle handling, dynamic weather, and competitive online play.',
    price: 44.99,
    priceCents: 4499,
    isFree: false,
    image: velocityRush,
    category: 'Games',
    rating: 4.4,
    reviews: 15632,
    tags: ['Racing', 'Multiplayer', 'Simulation', 'Sports'],
    size: '55.2 GB',
    releaseDate: '2025-09-05',
    featured: false,
    version: '1.0.0',
    packageType: 'none',
    packagePath: null,
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
  },
  {
    id: 'codevault',
    sku: 'codevault',
    name: 'CodeVault IDE',
    developer: 'DevTools Co.',
    description: 'The intelligent code editor for MapleOS. Built for speed, designed for developers.',
    longDescription: 'CodeVault is a lightweight yet powerful IDE that supports modern developer workflows with intelligent completions, collaboration, and profiling tools.',
    price: 'Free',
    priceCents: 0,
    isFree: true,
    image: codevault,
    category: 'Developer',
    rating: 4.8,
    reviews: 28456,
    tags: ['IDE', 'Code Editor', 'Developer Tools', 'Programming'],
    size: '340 MB',
    releaseDate: '2025-02-14',
    featured: true,
    version: '1.0.0',
    packageType: 'bundle',
    packagePath: 'products/codevault/1.0.0/index.html',
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
    supportedMapleOSEditions: ['mapleos-26', 'mapleos-pro-26', 'mapleos-enterprise-26'],
  },
  {
    id: 'harvest-valley',
    sku: 'harvest-valley',
    name: 'Harvest Valley',
    developer: 'Cozy Pixel Games',
    description: 'Build your dream farm, befriend the townsfolk, and discover the mysteries of the valley.',
    longDescription: 'Harvest Valley blends relaxing farming gameplay, heartfelt character stories, and a valley full of secrets to uncover.',
    price: 24.99,
    priceCents: 2499,
    isFree: false,
    image: harvestValley,
    category: 'Games',
    rating: 4.9,
    reviews: 42103,
    tags: ['Simulation', 'Farming', 'Indie', 'Cozy'],
    size: '3.2 GB',
    releaseDate: '2025-04-18',
    featured: false,
    version: '1.0.0',
    packageType: 'none',
    packagePath: null,
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
  },
  {
    id: 'formcraft-3d',
    sku: 'formcraft-3d',
    name: 'FormCraft 3D',
    developer: 'Precision Software',
    description: 'Professional 3D modeling and CAD software for architects, engineers, and designers.',
    longDescription: 'FormCraft 3D provides industry-standard parametric modeling, rendering, and simulation tools built for MapleOS.',
    price: 79.99,
    priceCents: 7999,
    isFree: false,
    image: formcraft,
    category: 'Creative',
    rating: 4.3,
    reviews: 4521,
    tags: ['3D Modeling', 'CAD', 'Architecture', 'Engineering'],
    size: '5.6 GB',
    releaseDate: '2025-06-30',
    featured: false,
    version: '1.0.0',
    packageType: 'none',
    packagePath: null,
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
  },
  {
    id: 'neon-district',
    sku: 'neon-district',
    name: 'Neon District',
    developer: 'Cyber Forge Entertainment',
    description: 'Survive the neon-lit streets of a dystopian megacity in this action RPG.',
    longDescription: 'Neon District drops you into a sprawling cyberpunk city where megacorps rule and hackers fight for freedom.',
    price: 54.99,
    priceCents: 5499,
    isFree: false,
    image: neonDistrict,
    category: 'Games',
    rating: 4.7,
    reviews: 21340,
    tags: ['Action RPG', 'Cyberpunk', 'Open World', 'Sci-Fi'],
    size: '67.8 GB',
    releaseDate: '2025-10-01',
    featured: false,
    version: '1.0.0',
    packageType: 'none',
    packagePath: null,
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
  },
  {
    id: 'datagrid-pro',
    sku: 'datagrid-pro',
    name: 'DataGrid Pro',
    developer: 'Maple Software Co.',
    description: 'Advanced spreadsheet and data analytics platform with visualization and automation.',
    longDescription: 'DataGrid Pro goes beyond traditional spreadsheets with visualization, scripting, data connections, and report automation.',
    price: 19.99,
    priceCents: 1999,
    isFree: false,
    image: datagrid,
    category: 'Office',
    rating: 4.6,
    reviews: 11234,
    tags: ['Spreadsheet', 'Analytics', 'Data', 'Business'],
    size: '450 MB',
    releaseDate: '2025-07-12',
    featured: false,
    version: '1.0.0',
    packageType: 'none',
    packagePath: null,
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
  },
  {
    id: 'echoes-of-light',
    sku: 'echoes-of-light',
    name: 'Echoes of Light',
    developer: 'Luminous Games',
    description: 'A mesmerizing puzzle-platformer set in an ancient crystal cavern full of light-bending puzzles.',
    longDescription: 'Guide a lost spirit through breathtaking underground landscapes in Echoes of Light and manipulate beams of light to solve intricate puzzles.',
    price: 14.99,
    priceCents: 1499,
    isFree: false,
    image: echoesOfLight,
    category: 'Games',
    rating: 4.8,
    reviews: 9876,
    tags: ['Puzzle', 'Platformer', 'Indie', 'Adventure'],
    size: '6.4 GB',
    releaseDate: '2025-12-05',
    featured: false,
    version: '1.0.0',
    packageType: 'bundle',
    packagePath: 'products/echoes-of-light/1.0.0/index.html',
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
  },
  {
    id: 'demo-wasm',
    sku: 'demo-wasm',
    name: 'Demo WASM Lab',
    developer: 'Maple Market Labs',
    description: 'A tiny installable WebAssembly bundle that demonstrates inline base64 decoding inside a single-file HTML app.',
    longDescription: 'Demo WASM Lab is a developer-facing Maple Market sample that proves a self-contained bundle can inline and instantiate WebAssembly without external requests.',
    price: 'Free',
    priceCents: 0,
    isFree: true,
    image: codevault,
    category: 'Developer',
    rating: 4.7,
    reviews: 128,
    tags: ['WASM', 'Developer', 'Demo', 'Bundle'],
    size: '52 KB',
    releaseDate: '2026-03-27',
    featured: false,
    version: '1.0.0',
    packageType: 'bundle',
    packagePath: 'products/demo-wasm/1.0.0/index.html',
    includesWebInstaller: true,
    includesDesktopInstaller: false,
    includesBareMetalInstaller: false,
    supportedMapleOSEditions: ['mapleos-26', 'mapleos-pro-26', 'mapleos-enterprise-26'],
  },
];

export const mapleMarketFallbackApps: MapleMarketApp[] = fallbackCatalog.map((app) => ({
  ...app,
  heroImageUrl: null,
  thumbnailUrl: null,
  supportedMapleOSEditions: app.supportedMapleOSEditions ?? ['mapleos-26', 'mapleos-pro-26', 'mapleos-enterprise-26'],
  visibilityStatus: 'published',
  publishRequestStatus: 'approved',
  publishRequestedAt: null,
  publishRequestedBy: null,
  publishApprovedAt: null,
  publishApprovedBy: null,
  publishRejectedAt: null,
  publishRejectedBy: null,
  publishRejectionReason: null,
  firstPublishedAt: app.releaseDate,
  hasBeenPublished: true,
}));

const fallbackBySku = new Map(mapleMarketFallbackApps.map((app) => [app.sku, app]));
const fallbackById = new Map(mapleMarketFallbackApps.map((app) => [app.id, app]));

export function formatMapleMarketPrice(price: number | 'Free') {
  if (price === 'Free') return 'Free';
  return `$${price.toFixed(2)}`;
}

export function buildMapleMarketApp(product: MarketplaceProductRecord): MapleMarketApp {
  const fallback = fallbackBySku.get(product.sku) ?? fallbackById.get(product.id || '') ?? fallbackCatalog[0];
  const priceCents = typeof product.price_cents === 'number' ? product.price_cents : fallback.priceCents;
  const isFree = typeof product.is_free === 'boolean' ? product.is_free : priceCents <= 0;
  const image = product.thumbnail_url || product.hero_image_url || fallback.image || '/placeholder.svg';
  const supportedMapleOSEditions = product.supported_mapleos_editions?.length
    ? product.supported_mapleos_editions
    : fallback.supportedMapleOSEditions ?? ['mapleos-26', 'mapleos-pro-26', 'mapleos-enterprise-26'];

  return {
    id: product.id || fallback.id,
    sku: product.sku,
    name: product.name || fallback.name,
    developer: product.developer || fallback.developer,
    description: product.short_description || fallback.description,
    longDescription: product.long_description || fallback.longDescription,
    price: isFree ? 'Free' : priceCents / 100,
    priceCents,
    isFree,
    image,
    heroImageUrl: product.hero_image_url || null,
    thumbnailUrl: product.thumbnail_url || null,
    category: (product.category as MapleMarketCategory) || fallback.category,
    rating: Number(product.rating ?? fallback.rating),
    reviews: Number(product.reviews ?? fallback.reviews),
    tags: product.tags?.length ? product.tags : fallback.tags,
    size: product.size_text || fallback.size,
    releaseDate: product.release_date || fallback.releaseDate,
    featured: product.featured ?? fallback.featured,
    version: product.version || fallback.version,
    packageType: product.package_type || fallback.packageType,
    packagePath: product.package_path || fallback.packagePath,
    includesWebInstaller: product.includes_web_installer ?? fallback.includesWebInstaller,
    includesDesktopInstaller: product.includes_desktop_installer ?? fallback.includesDesktopInstaller,
    includesBareMetalInstaller: product.includes_bare_metal_installer ?? fallback.includesBareMetalInstaller,
    supportedMapleOSEditions,
    visibilityStatus: product.visibility_status || 'published',
    publishRequestStatus: product.publish_request_status || 'none',
    publishRequestedAt: product.publish_requested_at || null,
    publishRequestedBy: product.publish_requested_by || null,
    publishApprovedAt: product.publish_approved_at || null,
    publishApprovedBy: product.publish_approved_by || null,
    publishRejectedAt: product.publish_rejected_at || null,
    publishRejectedBy: product.publish_rejected_by || null,
    publishRejectionReason: product.publish_rejection_reason || null,
    firstPublishedAt: product.first_published_at || null,
    hasBeenPublished: product.has_been_published ?? false,
  };
}

export function getMapleMarketFallbackImageBySku(sku: string) {
  return fallbackBySku.get(sku)?.image ?? null;
}

export function getMapleMarketAppById(id: string) {
  return fallbackById.get(id);
}